import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import {
  FaArrowRightLong,
  FaChartLine,
  FaDiagramProject,
  FaLaptopCode,
  FaNetworkWired,
} from "react-icons/fa6";

export default function HomeBanner2() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  const capabilityAreas = [
    {
      icon: <FaChartLine size={18} />,
      title: "Consulting & Strategy",
      copy: "Clarify priorities and define execution roadmaps aligned with real operational demands.",
    },
    {
      icon: <FaDiagramProject size={18} />,
      title: "Systems Integration",
      copy: "Unify data, workflows, and platforms to remove friction and accelerate execution.",
    },
    {
      icon: <FaNetworkWired size={18} />,
      title: "Infrastructure",
      copy: "Build resilient, secure network environments designed for long-term scalability.",
    },
    {
      icon: <FaLaptopCode size={18} />,
      title: "Digital Products",
      copy: "Develop high-impact tools that directly improve service delivery and workflows.",
    },
  ];

  const serviceAreas = [
    "ICT Consulting & Strategy",
    "Systems Integration",
    "Network Infrastructure",
    "Software Development",
    "IT Support & Maintenance",
    "Digital Transformation",
  ];

  useEffect(() => {
    setReady(true);
  }, []);

  if (!ready) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      <style>{`
        .tech-section-modern {
          position: relative;
          background: radial-gradient(circle at 20% 20%, #0f172a, #020617);
          color: #e2e8f0;
          padding: 6rem 1.5rem;
          overflow: hidden;
        }

        .tech-section-modern::before {
          content: "";
          position: absolute;
          width: 600px;
          height: 600px;
          background: rgba(59,130,246,0.15);
          filter: blur(120px);
          top: -100px;
          left: -100px;
        }

        .tech-container {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .tech-header {
          max-width: 720px;
          margin-bottom: 4rem;
        }

        .tech-eyebrow {
          font-size: 0.75rem;
          letter-spacing: 0.15em;
          color: #94a3b8;
          text-transform: uppercase;
        }

        .tech-title {
          font-size: 2.8rem;
          font-weight: 700;
          margin: 1rem 0;
          color: #ffffff;
        }

        .tech-copy {
          color: #94a3b8;
          font-size: 1.05rem;
        }

        /* Grid */
        .tech-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }

        @media (min-width: 1024px) {
          .tech-grid {
            grid-template-columns: 1.1fr 1fr;
          }
        }

        /* Glass panel */
        .tech-panel {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 18px;
          padding: 2rem;
          backdrop-filter: blur(12px);
        }

        .tech-panel-title {
          font-size: 1.4rem;
          color: #fff;
          margin: 0.5rem 0 1rem;
        }

        .tech-list {
          margin-top: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .tech-list-item {
          display: flex;
          gap: 1rem;
          font-size: 0.9rem;
          color: #cbd5f5;
        }

        .tech-btn {
          margin-top: 2rem;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.7rem 1.2rem;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.05);
          color: #e2e8f0;
          transition: all 0.3s ease;
        }

        .tech-btn:hover {
          background: rgba(59,130,246,0.2);
          transform: translateY(-2px);
        }

        /* Features */
        .tech-features {
          display: grid;
          gap: 1rem;
        }

        .tech-feature {
          display: flex;
          gap: 1rem;
          padding: 1.2rem;
          border-radius: 14px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          transition: all 0.3s ease;
        }

        .tech-feature:hover {
          background: rgba(59,130,246,0.1);
          transform: translateY(-3px);
        }

        .tech-icon {
          color: #60a5fa;
        }

        .tech-feature h4 {
          margin: 0;
          color: #fff;
        }

        .tech-feature p {
          margin: 0.3rem 0 0;
          font-size: 0.9rem;
          color: #94a3b8;
        }

        /* Service band */
        .tech-services {
          margin-top: 4rem;
          padding: 2rem;
          border-radius: 18px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
        }

        .tech-service-grid {
          margin-top: 1.5rem;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .tech-service-item {
          padding: 0.8rem 1rem;
          border-radius: 10px;
          background: rgba(255,255,255,0.04);
          font-size: 0.9rem;
          color: #cbd5e1;
          transition: 0.3s;
        }

        .tech-service-item:hover {
          background: rgba(59,130,246,0.15);
        }
      `}</style>

      <section className="tech-section-modern">
        <div className="tech-container">
          {/* Header */}
          <div className="tech-header">
            <h2 className="tech-title text-light">
              Purpose-built for {" "} <span className="text-secondary">modern technology environments</span> and real use cases.            
            </h2>
            <p className="tech-copy">
              Nova Technologies delivers structured, scalable, and practical
              technology systems that keep organizations efficient, connected,
              and future-ready.
            </p>
          </div>

          {/* Grid */}
          <div className="tech-grid">
            {/* Left Panel */}
            <div className="tech-panel">
              <h2 className="font-bold text-secondary">
                Structured execution with long-term reliability.
              </h2>

              <div className="tech-list">
                <div className="tech-list-item">
                  <strong>01</strong>
                  <span>Align technology with measurable business outcomes.</span>
                </div>
                <div className="tech-list-item">
                  <strong>02</strong>
                  <span>Implement with minimal disruption.</span>
                </div>
                <div className="tech-list-item">
                  <strong>03</strong>
                  <span>Maintain performance through ongoing support.</span>
                </div>
              </div>

              <button
                className="btn btn-primary btn-lg mt-5"
                onClick={() => appContext.navTo({ item: "about_us" })}
              >
                Learn More &nbsp;
                <FaArrowRightLong size={14} />
              </button>
            </div>

            {/* Right Features */}
            <div className="tech-features">
              {capabilityAreas.map((area) => (
                <div key={area.title} className="tech-feature">
                  <div className="tech-icon">{area.icon}</div>
                  <div>
                    <h4>{area.title}</h4>
                    <p>{area.copy}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>
    </>
  );
}